import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import * as yup from "yup";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { NotificationContext } from "lib/context/NotificationContext";
import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import {
  useStatus,
  usePriorities,
  useSeverity,
  editIncident,
  populateMasterData,
  useIncident,
  useUsers,
  Attribute,
} from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import { FormHeader } from "components/form";
import Details from "components/incident/AddEdit/Details";
import Location from "components/incident/AddEdit/Location";
import Members from "components/incident/AddEdit/Members";
import IncidentProperties from "components/incident/AddEdit/Properties";
import { Icon, OverflowTooltip } from "components/common";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

const filterInexistentMembers = (
  serverUserData: User[] = [],
  initialMembers: User[] = [],
  initialAssigneeUser: User | undefined
) => {
  const newInitialMembers = initialMembers.filter((im) => {
    return serverUserData.some((sud) => sud.id === im.id);
  });
  const filtered = initialMembers.length - newInitialMembers.length;
  const assignee =
    initialAssigneeUser != null
      ? newInitialMembers.find((nim) => nim.id === initialAssigneeUser.id)
      : undefined;
  return {
    filtered,
    members: newInitialMembers,
    assignee,
  };
};

const Edit: React.FC = () => {
  const { t } = useTranslation("incidentAddEdit");
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [, setPreventNavigation] = usePreventNavigation();

  const { setShowBanner, setBannerMessage, setBannerVariant } =
    useContext(NotificationContext);

  const { data: incidentData, mutate } = useIncident(id);

  const sections = [
    { label: t("details.title"), value: "details" },
    { label: t("location.title"), value: "location" },
    { label: t("members.section"), value: "members" },
    {
      label: t("properties.title"),
      value: "properties",
    },
  ];

  const validationSchema = useMemo(
    () =>
      yup.object().shape(
        {
          name: yup.string().trim().required(t("validation.required")),
          type: yup.number().required(t("validation.required")),
          status: yup.number().required(t("validation.required")),
          priority: yup.number().required(t("validation.required")),
          severity: yup.number().required(t("validation.required")),
          address: yup.string().when(["latitude", "longitude"], {
            is: (latitude: string, longitude: string) =>
              latitude != null && longitude != null,
            then: yup.string().required(t("validation.addressGeocodeError")),
          }),
          latitude: yup
            .number()
            .typeError(t("validation.latitudeOutOfRange"))
            .min(-90, t("validation.latitudeOutOfRange"))
            .max(90, t("validation.latitudeOutOfRange"))
            .when(["longitude", "findCameras"], {
              is: (longitude: string, findCameras: boolean) =>
                findCameras || longitude != null,
              then: yup
                .number()
                .typeError(t("validation.latitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          longitude: yup
            .number()
            .typeError(t("validation.longitudeOutOfRange"))
            .min(-180, t("validation.longitudeOutOfRange"))
            .max(180, t("validation.longitudeOutOfRange"))
            .when(["latitude", "findCameras"], {
              is: (latitude: string, findCameras: boolean) =>
                findCameras || latitude != null,
              then: yup
                .number()
                .typeError(t("validation.longitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          findCameras: yup.boolean(),
          radius: yup.number().when("findCameras", {
            is: true,
            then: yup
              .number()
              .typeError(t("validation.radiusNotPositiveNumber"))
              .positive(t("validation.radiusNotPositiveNumber"))
              .required(t("validation.required")),
          }),
          attributes: yup.array().of(
            yup.object().shape({
              label: yup.string().required(t("validation.required")),
              type: yup.string().nullable(),
              value: yup
                .mixed()
                .when("type", {
                  is: "boolean",
                  then: yup.boolean(),
                })
                .when("type", {
                  is: "numeric",
                  then: yup.number().typeError(t("validation.valueNotANumber")),
                })
                .when("type", {
                  is: "hyperlink",
                  then: yup.string().url(t("validation.iconUrlNotValid")),
                }),
            })
          ),
        },
        [["latitude", "longitude"]]
      ),
    [t]
  );

  const onSave = (data: IncidentFormData) => {
    const lat = Number.parseFloat(data.latitude as unknown as string);
    const lng = Number.parseFloat(data.longitude as unknown as string);
    const radius = Number.parseFloat(data.radius as unknown as string);

    const hasLocation = !Number.isNaN(lat) && !Number.isNaN(lng);

    const attributes: Record<string, Attribute> = {};
    if (data.attributes && data.attributes.length > 0)
      data.attributes?.forEach((attr: Attribute) => {
        // exclude id from the object sent to the server
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: attrId, type, value, ...others } = attr;
        let formattedFinalDate;
        if (type === "date and time") {
          // if (value !== incidentData?.data.attributes[`${attr.id}`]?.value) {
          formattedFinalDate = value;
          /* const objData = new Date(value as string);
            const objDate = {
              date: moment(objData).format("YYYY-MM-DD"),
              hours: moment(objData).get("hours"),
              minutes: moment(objData).get("minutes"),
              seconds: moment(objData).get("seconds"),
            };

            // eslint-disable-next-line camelcase
            const final_date = moment(
              `${objDate.date} ${objDate.hours}:${objDate.minutes}:${objDate.seconds}`
            ).format(standardDateFormat);
            formattedFinalDate = moment(
              getDateTimeInUTCByZoneWithoutFormat(final_date, timezone)
            ).format(standardDateFormat);
          } else {
            formattedFinalDate = value as string;
          } */
        }
        attributes[`${attr.id}`] = {
          ...others,
          type,
          value:
            value && type === "numeric"
              ? Number(value)
              : type === "date and time"
              ? formattedFinalDate
              : value,
          contentType: attr.contentType ? attr.contentType : undefined,
        };
      });

    return editIncident({
      id,
      pk_inc_id: incidentData?.data.pk_inc_id,
      inc_name: data.name?.trim(),
      fk_inc_status_id: data.status,
      fk_inc_priority_id: data.priority,
      fk_inc_severity_id: data.severity,
      fk_inc_type_id: data.type,
      inc_assigned_user: data.assignedUser as User,
      inc_watchlist: data.members as User[],
      inc_desc: data.description?.trim(),
      ...(hasLocation
        ? {
            inc_address: data.address,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            nearest_cameras_dist_in_miles: data.findCameras ? radius : 0,
          }
        : {}),
      attributes,
    });
  };

  const { data: userData } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
    }
  );

  const attrKeys = Object.keys(incidentData?.data.attributes || {});
  const attrValues = Object.values(incidentData?.data.attributes || {});

  const initialValues = useMemo(
    () => ({
      name: incidentData?.data.inc_name || "",
      description: incidentData?.data.inc_desc || "",
      address: incidentData?.data.inc_address || "",
      type: incidentData?.data.fk_inc_type_id || "",
      status: incidentData?.data.fk_inc_status_id || "",
      priority: incidentData?.data.fk_inc_priority_id || "",
      severity: incidentData?.data.fk_inc_severity_id || "",
      assignedUser: incidentData?.data.inc_assigned_user as User,
      members: incidentData?.data.inc_watchlist as User[],
      ...(incidentData?.data.location
        ? {
            longitude: incidentData.data.location.coordinates[0],
            latitude: incidentData.data.location.coordinates[1],
            // eslint-disable-next-line no-unneeded-ternary
            findCameras: incidentData.data.nearest_cameras_dist_in_miles
              ? true
              : false,
            radius: incidentData.data.nearest_cameras_dist_in_miles ?? null,
          }
        : {}),
      attributes: attrValues.map((attr, index) => ({
        id: attrKeys[index],
        ...attr,
      })),
    }),
    [
      incidentData?.data.fk_inc_priority_id,
      incidentData?.data.fk_inc_severity_id,
      incidentData?.data.fk_inc_status_id,
      incidentData?.data.fk_inc_type_id,
      incidentData?.data.inc_address,
      incidentData?.data.inc_assigned_user,
      incidentData?.data.inc_desc,
      incidentData?.data.inc_name,
      incidentData?.data.inc_watchlist,
      incidentData?.data.location,
      incidentData?.data.nearest_cameras_dist_in_miles,
      attrKeys,
      attrValues,
    ]
  );

  const [cleanedValues, setCleanedValues] = useState<IncidentFormData>();

  const didTheCleaning = useRef(false);
  const didSetValues = useRef(false);
  useEffect(() => {
    if (userData && !didTheCleaning.current) {
      if (initialValues.members != null && initialValues.members.length > 0) {
        didTheCleaning.current = true;

        const serverUserData = userData.data as User[];
        const newInitialMembers = filterInexistentMembers(
          serverUserData,
          initialValues.members,
          initialValues.assignedUser
        );
        if (newInitialMembers.filtered > 0) {
          setBannerVariant("warning");
          setBannerMessage(
            t("validation.removedUsers", { count: newInitialMembers.filtered })
          );
          setShowBanner(true);
          const newInitialValues = { ...initialValues };
          newInitialValues.members = newInitialMembers.members;
          newInitialValues.assignedUser = newInitialMembers.assignee as User;
          setCleanedValues(newInitialValues as IncidentFormData);
        }
      }
    }
  }, [
    t,
    userData,
    initialValues,
    setShowBanner,
    setBannerMessage,
    setBannerVariant,
  ]);

  const onSubmitHandler = async (
    values: IncidentFormData,
    actions: FormikHelpers<IncidentFormData>
  ) => {
    try {
      mutate(await onSave(values));
      enqueueSnackbar(t("validation.success.update"), { variant: "success" });
      setPreventNavigation(false);
      history.push("/settings/incident");
      actions.setStatus({});
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const message = error?.response?.data?.error?.message;
      if (errorCode?.endsWith("_name_already_taken")) {
        actions.setStatus({
          serverSideErrors: {
            name: t(`validation.nameAlreadyTaken`),
          },
        });
      } else if (
        errorCode === "Incident_invalid_operation" &&
        message === "cannot_close_incident_with_open_sop"
      ) {
        setBannerMessage(t(`validation.cantCloseIncidentWithSOP`));
        setBannerVariant("error");
        setShowBanner(true);
      } else {
        setBannerVariant("error");
        setBannerMessage(t(`validation.unknownError.edit`));
        setShowBanner(true);
      }
    }
  };

  const { data: statusData, mutate: revalidateStatus } = useStatus({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: priorityData, mutate: revalidatePriority } = usePriorities({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: severityData, mutate: revalidateSeverity } = useSeverity({
    revalidateOnFocus: false,
    suspense: true,
  });

  const calledPopulateMasterData = useRef(false);

  const noStaticData =
    statusData?.data?.length === 0 &&
    priorityData?.data?.length === 0 &&
    severityData?.data?.length === 0;

  useEffect(() => {
    if (noStaticData && !calledPopulateMasterData.current) {
      calledPopulateMasterData.current = true;

      (async () => {
        await populateMasterData();

        await Promise.all([
          revalidateStatus(),
          revalidatePriority(),
          revalidateSeverity(),
        ]);
      })();
    }
  }, [noStaticData, revalidatePriority, revalidateSeverity, revalidateStatus]);

  const innerClasses = useMemo(
    () =>
      makeStyles(() => ({
        background: {
          backgroundColor: incidentData?.data.appearance?.color,
          "& svg": {
            color: "white",
          },
        },
      })),
    [incidentData?.data.appearance]
  )();

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as IncidentFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting, setFieldValue } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        if (cleanedValues != null && !didSetValues.current) {
          didSetValues.current = true;
          setFieldValue("assignedUser", cleanedValues.assignedUser, false);
          setFieldValue("members", cleanedValues.members, false);
        }
        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={
                  <>
                    <div
                      className={clsx(
                        classes.formHeaderIcon,
                        innerClasses.background
                      )}
                    >
                      <Icon
                        iconData={incidentData?.data.appearance || {}}
                        {...(incidentData?.data.appearance &&
                          incidentData?.data.appearance?.icon_type ===
                            "fontawesome" && {
                            color: "#FFFFFF",
                          })}
                      />
                    </div>
                    <OverflowTooltip
                      data={incidentData?.data.inc_name as string}
                      className={clsx(classes.title)}
                    />
                  </>
                }
                sections={sections}
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details mode="edit" />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <Location />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <Members />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[3].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <IncidentProperties {...arrayHelpers} />
                    )}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const IncidentEditAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(
      ModuleNames.incidentManagement,
      ModulePolicyTypes.incident
    );
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Edit />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Edit;
